import { CircleX, FileInput, Trash2 } from "lucide-react"
import { Button } from "@components/common/button"
import { Drawer } from "@components/common/drawer"
import useTranslator from "@hooks/use-translator"
import { HTMLAttributes, useState } from "react"
import DeleteTask from "./delete-task"
import TaskBatchStatusEditor from "./task-batch-status-editor"
import BatchAssignToUser from "./batch-assign-to-user"
import TransferProject from "./transfer-project"
import MoveTask from "./grouped-tasks/Move-task-group"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import RestoreTask from "./restore-task"

interface Props extends HTMLAttributes<HTMLDivElement> {
  onCancel: Function
  tasks?: Array<{
    taskId: number
    priorityId: number
    responsible_id: number
    project_id: number
    group_id: number
  }>
}

export default function TableBatchActions({ tasks = [], onCancel }: Props) {
  const { translate, locale } = useTranslator()

  const [isOpen, setIsOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isTaskGroupModalOpen, setIsTaskGroupModalOpen] = useState(false)

  const handleOpenTaskGroupModal = () => {
    if (tasks.length > 0) {
      setIsTaskGroupModalOpen(true)
    }
  }

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <div className='fixed bottom-4 left-1/2 z-[100] flex w-fit -translate-x-1/2 transform flex-row items-center justify-between rounded-md border-2 border-white bg-primary/90 p-1 sm:max-w-[90%]'>
        {tasks.length > 0 && (
          <>
            {isModalOpen && (
              <TransferProject
                IsModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                tasks={tasks}
                project_id={tasks[0].project_id}
              />
            )}
            {isTaskGroupModalOpen && (
              <MoveTask
                IsModalOpen={isTaskGroupModalOpen}
                setIsModalOpen={setIsTaskGroupModalOpen}
                tasks={tasks}
                group_id={tasks[0].group_id}
              />
            )}
            <div className='flex flex-row items-center justify-evenly gap-3 overflow-x-auto p-3'>
              <div className='text-wrap-nowrap flex md:justify-between'>
                <div className='text-sm font-semibold text-white'>
                  <span>{`${translate("Selected")} `}</span>
                  <span>{tasks.length}</span>{" "}
                  <span>{` ${tasks.length === 1 ? translate("task") : translate("tasks")}`}</span>
                </div>
              </div>
              <div className='flex flex-row gap-2'>
                <div className='flex items-center rounded-sm text-xs'>
                  <RestoreTask className='flex flex-row items-center justify-between' tasks={tasks}>
                    <Button
                      variant='outline'
                      className={`flex flex-row gap-2 ${locale === "he" ? "sm:w-[120px] md:w-min" : "sm:w-[110px] md:w-min"} items-center justify-between  hover:bg-slate-300`}
                      size={"sm"}
                    >
                      <span className='hidden min-w-[35px] flex-1 text-left sm:block'>
                        {`${translate("Restore")} ${tasks.length === 1 ? translate("task") : translate("tasks")}`}
                      </span>
                    </Button>
                  </RestoreTask>
                </div>
                <div className='flex items-center rounded-sm text-xs text-red-500'>
                  <DeleteTask className='flex flex-row items-center justify-between' tasks={tasks}>
                    <Button
                      variant='outline'
                      className={`flex flex-row gap-2 ${locale === "he" ? "sm:w-[120px] md:w-min" : "sm:w-[110px] md:w-min"} items-center justify-between text-red-400 hover:bg-slate-300`}
                      size={"sm"}
                    >
                      <Trash2 size={14} />
                      <span className='hidden min-w-[35px] flex-1 text-left sm:block'>
                        {`${translate("Delete")} ${tasks.length === 1 ? translate("task") : translate("tasks")}`}
                      </span>
                    </Button>
                  </DeleteTask>
                </div>
                <div>
                  <TaskBatchStatusEditor taskId={tasks} />
                </div>
                <div>
                  <BatchAssignToUser taskArr={tasks} />
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        onClick={() => setIsModalOpen(!isModalOpen)}
                        variant={"outline"}
                        className='flex flex-row items-center justify-center hover:bg-slate-300'
                        size={"sm"}
                      >
                        <div className='flex w-full items-center gap-2'>
                          <FileInput size={14} />
                          <span className='hidden min-w-[35px] flex-1 text-left sm:block'>
                            {`${tasks.length === 1 ? translate("Move task to other project") : translate("Move tasks to other project")}`}
                          </span>
                        </div>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className='flex items-center gap-1 bg-destructive'>
                      {<h4>{translate(`Move selected tasks to another project`)}</h4>}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div>
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        onClick={handleOpenTaskGroupModal}
                        variant={"outline"}
                        className='flex flex-row items-center justify-center hover:bg-slate-300'
                        size={"sm"}
                      >
                        <div className='flex w-full items-center gap-2'>
                          <FileInput size={14} />
                          <span className='hidden min-w-[35px] flex-1 text-left sm:block'>
                            {`${tasks.length === 1 ? translate("Move task to other group") : translate("Move tasks to other group")}`}
                          </span>
                        </div>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className='flex items-center gap-1 bg-destructive'>
                      <h4>{`${tasks.length === 1 ? translate("Move selected task to other group") : translate("Move selected tasks to other group")}`}</h4>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className=''>
                <Button
                  variant={"destructive"}
                  className='text-black-400 flex-row gap-1 px-3'
                  onClick={() => onCancel()}
                >
                  <CircleX size={14} />
                  <span className='hidden min-w-[35px] flex-1 text-left sm:block'>
                    {translate("Clear selection")}
                  </span>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Drawer>
  )
}
